import { FC, useState, createElement, SVGProps, useMemo } from "react";

import { FormattedMessage } from "react-intl";

import { Button } from "@/components/atoms/Button";
import { Text } from "@/components/atoms/Text";
import { ExpandableContent } from "@/components/molecules/ExpandableContent";
import { cn } from "@/lib/utils";
import { useListingData } from "~/context/ListingPageDataContext";
import { useTransactable } from "~/hooks/use-transactable";
import useRedirectToSigninSignup from "~/hooks/useRedirectToSigninSignup";
import { IAmenity, IStructuredAmenity } from "~/types/api-types";
import { loadDynamicIcon } from "~/utils/loadDynamicIcon";

export interface ListingAmenitiesProps {
  amenities: Record<string, IStructuredAmenity[]>;
  ownerFirstName: string;
  listingId: string;
}

export interface MissingAmenityProps {
  ownerFirstName: string;
  listingId: string;
}

// Component for rendering an individual amenity
const AmenityItem = ({ amenity }: { amenity: IAmenity }) => {
  const icon = amenity.icon;
  const missing = amenity.missing;
  const IconComponent = useMemo(
    () => (icon ? loadDynamicIcon(icon, 16) : null),
    [icon],
  );

  return (
    <div
      className={cn("amenity-item tw-w-2/4 tw-flex tw-items-center tw-mb-16", {
        "tw-text-gray-600": missing,
      })}
    >
      <Text
        prefix={
          IconComponent &&
          createElement(IconComponent, {
            role: "img",
            "aria-label": icon,
          } as SVGProps<SVGSVGElement>)
        }
      >
        <p
          className={cn({
            "tw-line-through": missing,
          })}
        >
          {amenity.localizedName || amenity.name}
        </p>
      </Text>
    </div>
  );
};

const MissingAmenity: FC<MissingAmenityProps> = ({
  ownerFirstName,
  listingId,
}) => {
  const { checkTransactable } = useTransactable();
  const {
    data: { listingStatus },
  } = useListingData();
  const { handleRedirectToSignupPage, isUserLoggedIn } =
    useRedirectToSigninSignup();
  const isListingPaused =
    listingStatus === "PAUSED" || listingStatus === "PAUSED_INTERNALLY";

  const handleMessageHost = () => {
    const url = `/inquiry/${listingId}`;
    if (!isUserLoggedIn) {
      handleRedirectToSignupPage("msg", url);
    } else {
      checkTransactable(() => {
        window.location.href = url;
        return;
      });
    }
  };

  if (isListingPaused) {
    return null;
  }

  return (
    <Text className="text" data-testing-id="askTheHostLink">
      <FormattedMessage
        id="listing.listing_amenities.misc_amenities"
        defaultMessage="Don't see an amenity you're looking for? {link}"
        values={{
          link: (
            <Button
              onClick={handleMessageHost}
              variant="text-only"
              appearance="purple"
            >
              <FormattedMessage
                id="listing.listing_amenities.ask_the_host"
                defaultMessage="Ask the host, {ownerFirstName}"
                values={{
                  ownerFirstName,
                }}
              />
            </Button>
          ),
        }}
      />
    </Text>
  );
};

const AmenityGroup = ({
  amenities,
  subheader,
}: {
  amenities: IAmenity[];
  subheader?: string;
}) => (
  <>
    {subheader && (
      <Text element="h3" typeface="body-2-m" className="tw-pb-16">
        {subheader}
      </Text>
    )}
    <div className="tw-flex tw-flex-wrap">
      {amenities.map((amenity, index) => (
        <AmenityItem key={`${amenity.name}-${index}`} amenity={amenity} />
      ))}
    </div>
  </>
);

export const ListingAmenities = ({
  amenities,
  ownerFirstName,
  listingId,
}: ListingAmenitiesProps) => {
  const [contentExpanded, setContentExpanded] = useState(false);
  const subheaderOrder = ["Features", "Amenities", "Other"];
  const sortedAmenitySections = subheaderOrder.filter(
    (category) => amenities[category],
  );

  const handleExpansion = (expanded: boolean) => {
    if (expanded) {
      window.analyticsIntegrations.trackEvent("amenities expanded", {
        listingId,
      });
    }
    setContentExpanded(expanded);
  };

  // The amenities are already grouped by subheader, so we can use them directly
  const firstSubheader = sortedAmenitySections[0];
  const remainingSubheaders = sortedAmenitySections.slice(1);
  const initialAmenities = amenities[firstSubheader].slice(0, 4);
  const remainingInitialAmenities = amenities[firstSubheader].slice(4);

  const renderExpandableContent = () => {
    try {
      return (
        <ExpandableContent
          classNames="tw-mt-24 tw-w-full tw-text-left tw-flex tw-justify-start"
          openTriggerLabel={
            <span data-testing-id="showAllAmenitiesButton">
              <FormattedMessage
                id="listing.collapsing_container.show_all"
                defaultMessage="Show all"
              />
            </span>
          }
          closeTriggerLabel={
            <span data-testing-id="showLessAmenitiesButton">
              <FormattedMessage
                id="listing.collapsing_container.show_less"
                defaultMessage="Show less"
              />
            </span>
          }
          expandDown={true}
          triggerShownBelowContent={true}
          hideButtonOnOpen={false}
          onHandleToggle={handleExpansion}
          buttonAppearance="black"
          buttonVariant="underline"
        >
          {remainingInitialAmenities.length > 0 && (
            <div className="tw-flex tw-flex-wrap">
              {remainingInitialAmenities.map((amenity, index) => (
                <AmenityItem
                  key={`${amenity.name}-${index}`}
                  amenity={amenity}
                />
              ))}
            </div>
          )}
          {remainingSubheaders.map((subheader) => (
            <>
              {amenities[subheader].length > 0 && (
                <AmenityGroup
                  key={subheader}
                  subheader={subheader}
                  amenities={amenities[subheader]}
                />
              )}
            </>
          ))}
          <div className="tw-mb-12">
            <MissingAmenity
              ownerFirstName={ownerFirstName}
              listingId={listingId}
            />
          </div>
        </ExpandableContent>
      );
    } catch {
      return <div>Error loading amenities.</div>;
    }
  };

  // if there are no amenities, don't render anything
  if (!amenities || Object.keys(amenities).length === 0) {
    return null;
  }

  return (
    <div className="ListingAmenities tw-my-40" id="amenities_section">
      <Text
        element="h2"
        typeface="headline-2-m"
        className="tw-my-12"
        data-testing-id="amenitiesSectionTitle"
      >
        <FormattedMessage
          id="listing.listing_amenities.include_your_booking"
          defaultMessage="Included in your booking"
        />
      </Text>
      <div className="tw-relative tw-w-full">
        <AmenityGroup amenities={initialAmenities} subheader={firstSubheader} />
        <div
          className={cn(
            "tw-z-10 tw-absolute tw-bottom-0 tw-left-0 tw-h-64 tw-w-full tw-bg-gradient-to-t tw-from-base-white tw-to-transparent",
            { "tw-hidden": contentExpanded },
          )}
        ></div>
      </div>
      {renderExpandableContent()}
    </div>
  );
};
